import {flow, isEmpty} from 'lodash/fp';
import React, {Component, ComponentClass, ReactNode} from 'react';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {Opt, opt} from 'ts-opt';
import {todayDate} from 'favorlogic-utils';

import {Components as Layout} from 'layout';
import {createMeasurements, resetSearchSamples, searchSamples, loadRequestedDataItems} from 'measurement/actions';
import {SelectOptions} from 'forms/components/BasicSelect';
import {StoreState} from 'app/types/StoreState';
import {DataItem} from 'types/model/dataItems/DataItem';
import {SampleSearch} from 'types/model/samples/SampleSearch';
import {UserDetails} from 'types/model/users/UserDetails';
import {OperatingProcedure} from 'types/model/operatingProcedures/OperatingProcedure';
import {loadLaborers} from 'user/actions';
import withUser, {WithUserProps} from 'user/components/withUser';
import {formValuesF, initializeF} from 'utils/formHelpers';
import {loadDataItems} from 'dataItem/actions';
import {loadOperatingProcedures} from 'operatingProcedure/actions';

import DataItemMeasurementsForm, {measurementsInitialValues} from '../components/DataItemMeasurementsForm';
import {DataItemMeasurementsFormValues} from '../type/DataItemMeasurementsFormValues';
import {buildDataItemOptions} from '../utils/buildDataItemOptions';
import {buildUserOptions} from '../utils/buildUserOptions';
import {buildProcedureOptions} from '../utils/buildProcedureOptions';

interface OuterProps {}

interface StateProps {
    samples: Opt<SampleSearch[]>;
    laborers: Opt<UserDetails[]>;
    dataItems: DataItem[] | null;
    procedures: OperatingProcedure[] | null;
    selectedDataItem?: DataItem;
    requestedDataItemIds: number[];
}

interface DispatchProps {
    handleLoadRequestedDataItems(): void;
    handleLoadLaborers(): void;
    handleSearchSamples(dataItemId: number | null): void;
    handleLoadDataItems(): void;
    handleLoadProcedures(): void;
    handleCreateMeasurements(): void;
    initializeForm(values: DataItemMeasurementsFormValues): void;
}

type UserProps = Pick<WithUserProps, 'currentUser'>;
type Props = OuterProps & StateProps & DispatchProps & UserProps;

class NewDataItem extends Component<Props> {
    componentDidMount(): void {
        const {
            handleLoadLaborers,
            handleLoadDataItems,
            handleLoadRequestedDataItems,
            handleLoadProcedures,
            initializeForm,
            currentUser,
        } = this.props;

        initializeForm({
            ...measurementsInitialValues,
            measured: todayDate(),
            measuredById: currentUser.orCrash('missing user').id,
        });
        handleLoadLaborers();
        handleLoadDataItems();
        handleLoadRequestedDataItems();
        handleLoadProcedures();
    }

    render(): ReactNode {
        const {
            handleSearchSamples,
            handleCreateMeasurements,
            requestedDataItemIds,
            samples,
            selectedDataItem,
        } = this.props;

        return (
            <Layout.ItemPage
                title="Nové měření z datové položky"
                backLabel="Měření"
            >
                {isEmpty(requestedDataItemIds)
                    ? <Layout.Panel>
                        <div className="p-4 d-flex justify-content-center">
                            Všechny požadované datové položky byly naměřeny.
                        </div>
                    </Layout.Panel>
                    : <DataItemMeasurementsForm
                        dataItemOptions={this.buildDataItemOptions()}
                        userOptions={this.buildUserOptions()}
                        procedureOptions={this.buildProcedureOptions()}
                        onDataItemChanged={handleSearchSamples}
                        samples={samples.orNull()}
                        selectedDataItem={selectedDataItem}
                        onSubmit={handleCreateMeasurements}
                    />}
            </Layout.ItemPage >
        );
    }

    private buildDataItemOptions(): SelectOptions<DataItem> {
        const {dataItems, requestedDataItemIds} = this.props;

        return buildDataItemOptions(dataItems, requestedDataItemIds);
    }

    private buildUserOptions(): SelectOptions<number> {
        const {laborers} = this.props;

        return buildUserOptions(laborers);
    }

    private buildProcedureOptions(): SelectOptions<number> {
        const {procedures, selectedDataItem} = this.props;

        return buildProcedureOptions(procedures, opt(selectedDataItem));
    }
}

const mapStateToProps = (state: StoreState): StateProps => ({
    laborers: state.user.laborers,
    samples: state.measurement.samplesSearch,
    dataItems: state.dataItem.dataItems,
    procedures: state.operatingProcedure.operatingProcedures,
    requestedDataItemIds: state.measurement.requestedDataItems,
    selectedDataItem: formValuesF('dataItemMeasurements')(state)
        .chainToOpt(x => x.dataItem).orUndef(),
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    handleLoadLaborers: () => dispatch(loadLaborers()),
    handleSearchSamples: (dataItemId: number | null) =>
        opt(dataItemId).caseOf(
            id => {
                dispatch(resetSearchSamples());
                dispatch(searchSamples({dataItemId: id}));
            },
            () => { dispatch(resetSearchSamples()); },
        ),
    handleLoadDataItems: () => dispatch(loadDataItems()),
    handleLoadRequestedDataItems: () => dispatch(loadRequestedDataItems()),
    handleLoadProcedures: () => dispatch(loadOperatingProcedures()),
    handleCreateMeasurements: () => dispatch(createMeasurements()),
    initializeForm: (values: DataItemMeasurementsFormValues) => dispatch(initializeF('dataItemMeasurements', values)),
});

export default flow([
    connect(mapStateToProps, mapDispatchToProps),
    withUser,
])(NewDataItem) as ComponentClass<OuterProps>;
